import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { io } from 'socket.io-client';
import { supabase } from '../lib/supabase';
import { formatMessageTime } from '../lib/formatMessageTime';
import Loader from './Loader';
import './RequestPortal.css';

const API_BASE = 'https://mykaddle-api.onrender.com';

const RequestPortal = () => {
  const { subjectId } = useParams();
  const navigate = useNavigate();

  // ── State ──
  const [group, setGroup] = useState(null);
  const [trial, setTrial] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState(null); // 'tutor' | 'tutee'
  const [participants, setParticipants] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadToast, setUploadToast] = useState(null);

  // Rate modal
  const [rateModalOpen, setRateModalOpen] = useState(false);
  const [newRate, setNewRate] = useState('');

  // Leave modal (adopted from SubjectPortal)
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const plusButtonRef = useRef(null);

  const currentUser = JSON.parse(localStorage.getItem('user'));
  const currentUserId = currentUser?._id || currentUser?.id;
  const currentUserName = `${currentUser.firstName} ${currentUser.lastName}`;
  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  // ── Init: Fetch group info, check access, start trial ────
  useEffect(() => {
    const init = async () => {
      try {
        // Get group info
        const infoRes = await axios.get(`${API_BASE}/api/request-groups/${subjectId}/info`, { headers: authHeaders });
        const groupData = infoRes.data;
        setGroup(groupData);

        // ✅ Determine role with a LOCAL variable
        let role = 'tutee';
        if (groupData.isRequestBased) {
          try {
            const tutorRes = await axios.get(`${API_BASE}/api/tutor/profile`, { headers: authHeaders });
            const isTutor = tutorRes.data?._id?.toString() === groupData.tutorProfile?.toString();
            role = isTutor ? 'tutor' : 'tutee';
              } catch {
            // Not a tutor (or tutor profile doesn't exist) — must be tutee
            role = 'tutee';
          }
        }
        setUserRole(role);

        // Check access
        const accessRes = await axios.get(`${API_BASE}/api/request-groups/${subjectId}/access`, { headers: authHeaders });

        if (!accessRes.data.access) {
          setError(accessRes.data);
          setLoading(false);
          return;
        }

        // ✅ Use LOCAL role variable, not state
        if (role === 'tutee' && groupData.isRequestBased && groupData.isTrial && !groupData.trialStartedAt) {
          try {
            const trialRes = await axios.post(
              `${API_BASE}/api/request-groups/${subjectId}/start-trial`,
              {},
              { headers: authHeaders }
            );
            setTrial(trialRes.data);
          } catch (trialErr) {
            // If start-trial fails, use group data as fallback
            console.warn('start-trial endpoint not available, using group data');
            setTrial({
              trialStartedAt: groupData.trialStartedAt || new Date().toISOString(),
              trialEndsAt: groupData.trialEndsAt || new Date(Date.now() + 3 * 60 * 60 * 1000).toISOString()
            });
          }
        } else {
          setTrial({
            trialStartedAt: groupData.trialStartedAt,
            trialEndsAt: groupData.trialEndsAt
          });
        }

        setLoading(false);
      } catch (err) {
        console.error('Init error:', err);
        setError({ reason: 'error', message: 'Failed to load group' });
        setLoading(false);
      }
    };
    init();
  }, [subjectId]);

  // ── Fetch members ────────────────────────────────────────
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/request-groups/${subjectId}/members`, { headers: authHeaders });
        setParticipants(res.data || []);
      } catch (err) { console.error('Failed to load members', err); }
    };
    fetchMembers();
  }, [subjectId]);

  // ── Socket presence ──────────────────────────────────────
  useEffect(() => {
    const socket = io(API_BASE);
    socket.emit('joinGroup', { subjectId, userId: currentUserId });
    socket.on('onlineUsers', (users) => setOnlineUsers(users));
    return () => { socket.emit('leaveGroup', { subjectId, userId: currentUserId }); socket.disconnect(); };
  }, [subjectId]);

  // ── Fetch files ──────────────────────────────────────────
  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/request-groups/${subjectId}/files`, { headers: authHeaders });
        setFileList(res.data || []);
      } catch (err) { setFileList([]); }
    };
    fetchFiles();
  }, [subjectId]);

  // ── Supabase messages ────────────────────────────────────
  useEffect(() => {
    const fetchMessages = async () => {
      const { data } = await supabase
        .from('messages').select('*')
        .eq('subject_id', subjectId)
        .order('created_at', { ascending: true });
      setMessages(data || []);
    };
    fetchMessages();
  }, [subjectId]);

  useEffect(() => {
    const channel = supabase
      .channel(`messages-${subjectId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter: `subject_id=eq.${subjectId}` },
        (payload) => setMessages(prev => [...prev, payload.new]))
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'messages', filter: `subject_id=eq.${subjectId}` },
        (payload) => setMessages(prev => prev.filter(m => m.id !== payload.old.id)))
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [subjectId]);

  // ── Scroll ───────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  }, [messages]);

  // ── Send message ─────────────────────────────────────────
  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    await supabase.from('messages').insert([{
      subject_id: subjectId,
      sender_id: currentUserId,
      sender_name: currentUserName,
      recipient_id: 'group',
      recipient_name: group?.tutorName || group?.subjectDisplayName || 'Chat',
      content: newMessage.trim()
    }]);
    setNewMessage('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
  };

  // ── Delete message ───────────────────────────────────────
  const handleDeleteMessage = async (msg) => {
    if (msg.sender_id !== currentUserId) return;
    setMessages(prev => prev.filter(m => m.id !== msg.id));
    await supabase.from('messages').delete().eq('id', msg.id);
  };

  // ── File upload ──────────────────────────────────────────
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadToast({ message: `Uploading "${file.name}"…`, type: 'uploading' });
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API_BASE}/api/request-groups/${subjectId}/upload`, formData, { headers: authHeaders });
      setFileList(prev => [res.data, ...prev]);
      setUploadToast({ message: `"${file.name}" uploaded!`, type: 'success' });
      setTimeout(() => setUploadToast(null), 3000);
    } catch (err) {
      setUploadToast({ message: `Failed to upload "${file.name}"`, type: 'error' });
      setTimeout(() => setUploadToast(null), 3000);
    } finally {
      setUploading(false);
    }
    e.target.value = '';
  };

  // ── Dropdown ─────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (plusButtonRef.current && !plusButtonRef.current.contains(e.target) &&
          dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // ── Rate adjustment ──────────────────────────────────────
  const openRateModal = () => {
    setNewRate(group?.rate?.toString() || '');
    setRateModalOpen(true);
  };

  const updateRate = async () => {
    try {
      await axios.put(`${API_BASE}/api/request-groups/${subjectId}/update-rate`, {
        subjectId,
        rate: parseFloat(newRate)
      }, { headers: authHeaders });
      setGroup(prev => ({ ...prev, rate: parseFloat(newRate) }));
      setRateModalOpen(false);
      setUploadToast({ message: `Rate updated to R${newRate}/week`, type: 'success' });
      setTimeout(() => setUploadToast(null), 3000);
    } catch (err) {
      setUploadToast({ message: 'Failed to update rate', type: 'error' });
      setTimeout(() => setUploadToast(null), 3000);
    }
  };

  // ── Leave group (adopted from SubjectPortal) ─────────────
  const handleLeaveGroup = async () => {
    try {
      await axios.post(`${API_BASE}/api/request-groups/${subjectId}/leave`, {}, { headers: authHeaders });
      setShowLeaveModal(false);
      navigate(-1);
    } catch (err) {
      console.error('Failed to leave group', err);
      setShowLeaveModal(false);
      setUploadToast({ message: 'Failed to leave group', type: 'error' });
      setTimeout(() => setUploadToast(null), 3000);
    }
  };

  // ── Expiry dot ───────────────────────────────────────────
  const getDaysLeft = (createdAt) => {
    const expiresAt = new Date(new Date(createdAt).getTime() + 7 * 24 * 60 * 60 * 1000);
    return Math.max(0, Math.ceil((expiresAt - Date.now()) / (1000 * 60 * 60 * 24)));
  };

  // ── Loading ──────────────────────────────────────────────
  if (loading) {
    return (
      <div className="rp-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <Loader size="8em" />
      </div>
    );
  }

  // ── Blocked screen (adopted from SubjectPortal) ──────────
  if (error && !error.access) {
    return (
      <div className="rp-container">
        <div className="rp-wrapper">
          {/* Top Bar with Back Button */}
          <div className="rp-top-bar">
            <div className="rp-top-left-header">
              <div className="rp-header-row">
                <button className="rp-back-icon-btn" aria-label="Go back" onClick={() => navigate(-1)}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="0.5em" height="1em" viewBox="0 0 12 24">
                    <path d="M0 0h12v24H0z" fill="none" />
                    <path fill="currentColor" fillRule="evenodd" d="M10 19.438L8.955 20.5l-7.666-7.79a1.02 1.02 0 0 1 0-1.42L8.955 3.5L10 4.563L2.682 12z" />
                  </svg>
                </button>
                <span className="rp-page-title">Dashboard</span>
              </div>
            </div>
          </div>

          {/* Centered Content */}
          <div className="rp-blocked-content">
            {/* Lock Icon */}
            <div className="rp-blocked-icon-ring">
              <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                <path d="M0 0h24v24H0z" fill="none" />
                <g fill="none">
                  <path fill="currentColor" fillOpacity=".25" d="M4 12c0-.943 0-1.414.293-1.707S5.057 10 6 10h12c.943 0 1.414 0 1.707.293S20 11.057 20 12v6.038c0 .38 0 .571-.029.74a2 2 0 0 1-1.164 1.49c-.156.07-.341.116-.71.208c-1.238.31-1.857.464-2.476.578c-2.394.44-4.848.44-7.243 0c-.618-.114-1.237-.269-2.474-.578c-.37-.092-.555-.139-.71-.207a2 2 0 0 1-1.165-1.492C4 18.61 4 18.42 4 18.037z" />
                  <path stroke="currentColor" d="M16.5 10V9a4.5 4.5 0 1 0-9 0v1" />
                  <circle cx="12" cy="15" r="2" fill="currentColor" />
                  <path stroke="currentColor" strokeLinecap="round" d="M12 16v2.5" />
                </g>
              </svg>
            </div>

            {/* Title */}
            <div className="rp-blocked-title">Access Blocked</div>

            {/* Message */}
            <p className="rp-blocked-message">
              Your free trial for this request has ended, or your subscription has lapsed. Choose what you'd like to do below. If something doesn't feel right about this, <strong>contact support</strong>.
            </p>

            {/* Action Buttons */}
            <div className="rp-blocked-actions">
              <button className="rp-btn-leave" onClick={() => setShowLeaveModal(true)}>Leave Request</button>
              <button
                className="rp-btn-pay"
                onClick={() => navigate('/payment/checkout', {
                  state: { subjectId, subjectName: group?.subjectDisplayName, subscriptionAmount: group?.rate || 0, billingCycle: 'weekly' }
                })}
              >
                Pay to Stay
              </button>
            </div>

            {/* Support Link */}
            <span className="rp-support-link" onClick={() => navigate('/dashboard/support')}>Contact Support</span>
          </div>

          {/* Leave Confirmation Modal */}
          {showLeaveModal && (
            <div className="rp-modal-overlay rp-modal-open" onClick={() => setShowLeaveModal(false)}>
              <div className="rp-modal" onClick={e => e.stopPropagation()}>
                <button className="rp-modal-close" onClick={() => setShowLeaveModal(false)}>✕</button>

                <div className="rp-modal-icon-ring">
                  <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                    <path d="M0 0h24v24H0z" fill="none" />
                    <path fill="currentColor" d="M5.616 20q-.691 0-1.153-.462T4 18.384V15.27h1v3.115q0 .231.192.424t.423.192h12.77q.23 0 .423-.192t.192-.424V5.616q0-.231-.192-.424T18.384 5H5.616q-.231 0-.424.192T5 5.616V8.73H4V5.616q0-.691.463-1.153T5.616 4h12.769q.69 0 1.153.463T20 5.616v12.769q0 .69-.462 1.153T18.384 20zm5.23-4.077l-.707-.72l2.703-2.703H4v-1h8.842L10.14 8.796l.707-.719L14.77 12z" />
                  </svg>
                </div>

                <div className="rp-modal-title">Leave this request?</div>

                <p className="rp-modal-message">
                  Are you sure you want to leave this request? You can always request the same tutor again from Discover.
                </p>

                <div className="rp-modal-actions">
                  <button className="rp-btn-cancel" onClick={() => setShowLeaveModal(false)}>Cancel</button>
                  <button className="rp-btn-confirm" onClick={handleLeaveGroup}>Yes, leave request</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Main Portal ──────────────────────────────────────────
  return (
    <div className="rp-container">
      <div className="rp-wrapper">
        <div className="rp-portal-body">
          {/* Messenger Container */}
          <div className="rp-messenger-container">
            {/* Sidebar */}
            <div className="rp-messenger-sidebar">
              <div className="rp-sidebar-header"><h3>Participants</h3></div>
              <div className="rp-participants-list">
                {participants.map((member) => (
                  <div key={member.id} className="rp-participant">
                    <div className="rp-participant-name-row">
                      <span className={`rp-status-dot ${onlineUsers.includes(member.userId) ? 'online' : 'offline'}`}></span>
                      <span className="rp-participant-name">
                        {member.displayName}
                        {member.role === 'tutor' && <span className="rp-tutor-badge">(Tutor)</span>}
                      </span>
                    </div>
                    {/* Only tutor sees "Adjust rate" */}
                    {userRole === 'tutor' && member.role === 'tutor' && (
                      <div className="rp-adjust-rate-row" onClick={openRateModal}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 16 16">
                          <path d="M0 0h16v16H0z" fill="none" />
                          <path fill="currentColor" d="M6 9.5A2 2 0 0 1 7.937 11H13.5a.5.5 0 0 1 .09.992L13.5 12l-5.563.001a2 2 0 0 1-3.874 0L2.5 12a.5.5 0 0 1-.09-.992L2.5 11h1.563A2 2 0 0 1 6 9.5m0 1a1 1 0 1 0 0 2a1 1 0 0 0 0-2m4-8A2 2 0 0 1 11.937 4H13.5a.5.5 0 0 1 .09.992L13.5 5l-1.563.001a2 2 0 0 1-3.874 0L2.5 5a.5.5 0 0 1-.09-.992L2.5 4h5.563A2 2 0 0 1 10 2.5m0 1a1 1 0 1 0 0 2a1 1 0 0 0 0-2" />
                        </svg>
                        <span className="rp-adjust-rate-text">Adjust rate</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Chat Main */}
            <div className="rp-chat-main">
              <div className="rp-chat-header">
                <div className="rp-chat-header-row">
                  <button className="rp-chat-back-btn" onClick={() => navigate(-1)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="0.5em" height="1em" viewBox="0 0 12 24">
                      <path d="M0 0h12v24H0z" fill="none" />
                      <path fill="currentColor" fillRule="evenodd" d="M10 19.438L8.955 20.5l-7.666-7.79a1.02 1.02 0 0 1 0-1.42L8.955 3.5L10 4.563L2.682 12z" />
                    </svg>
                  </button>
                  <span className="rp-chat-title">{group?.subjectDisplayName || 'Chat'}</span>
                  {trial?.trialEndsAt && userRole === 'tutee' && (
                    <span className="rp-trial-badge">
                      Trial · {Math.max(0, Math.ceil((new Date(trial.trialEndsAt) - Date.now()) / 3600000))}h left
                    </span>
                  )}
                </div>
              </div>

              <div className="rp-messenger-body">
                {messages.length === 0 ? (
                  <div className="rp-empty-chat">
                    <div className="rp-empty-chat-text">Start the conversation</div>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isMine = msg.sender_id === currentUserId;
                    return (
                      <div key={msg.id} className={`rp-message ${isMine ? 'sent' : 'received'}`}>
                        <div className="rp-sender">{isMine ? 'You' : msg.sender_name}</div>
                        <div className="rp-bubble">{msg.content}</div>
                        <div className="rp-timestamp">
                          {isMine && (
                            <button className="rp-delete-btn" onClick={() => handleDeleteMessage(msg)}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
                                <path fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.5" d="m19.5 5.5l-.62 10.025c-.158 2.561-.237 3.842-.88 4.763a4 4 0 0 1-1.2 1.128c-.957.584-2.24.584-4.806.584c-2.57 0-3.855 0-4.814-.585a4 4 0 0 1-1.2-1.13c-.642-.922-.72-2.205-.874-4.77L4.5 5.5M3 5.5h18m-4.944 0l-.683-1.408c-.453-.936-.68-1.403-1.071-1.695a2 2 0 0 0-.275-.172C13.594 2 13.074 2 12.035 2c-1.066 0-1.599 0-2.04.234a2 2 0 0 0-.278.18c-.395.303-.616.788-1.058 1.757L8.053 5.5m1.447 11v-6m5 6v-6"/>
                              </svg>
                            </button>
                          )}
                          {formatMessageTime(msg.created_at)}
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="rp-messenger-footer">
                <div className="rp-message-input-container">
                  <textarea
                    ref={textareaRef}
                    className="rp-message-input"
                    placeholder="Type a message..."
                    rows="1"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <button className="rp-send-button" onClick={handleSendMessage}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24">
                    <path fill="currentColor" d="m20.34 9.32l-14-7a3 3 0 0 0-4.08 3.9l2.4 5.37a1.06 1.06 0 0 1 0 .82l-2.4 5.37A3 3 0 0 0 5 22a3.14 3.14 0 0 0 1.35-.32l14-7a3 3 0 0 0 0-5.36Zm-.89 3.57l-14 7a1 1 0 0 1-1.35-1.3l2.39-5.37a2 2 0 0 0 .08-.22h6.89a1 1 0 0 0 0-2H6.57a2 2 0 0 0-.08-.22L4.1 5.41a1 1 0 0 1 1.35-1.3l14 7a1 1 0 0 1 0 1.78"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Files Panel */}
          <div className="rp-right-bars">
            <div className="rp-files">
              <div className="rp-files-header">
                <span className="rp-files-title">Group files</span>
                <button className="rp-plus-button" ref={plusButtonRef} onClick={() => setDropdownOpen(!dropdownOpen)}>+</button>
              </div>
              <div className="rp-file-list">
                {fileList.length === 0 ? (
                  <div className="rp-empty-files">No files yet</div>
                ) : (
                  fileList.map((file) => {
                    const days = getDaysLeft(file.createdAt);
                    const dotClass = days >= 5 ? 'green' : days >= 3 ? 'yellow' : 'red';
                    return (
                      <div key={file._id} className="rp-file-item" onClick={() => window.open(file.fileUrl, '_blank')}>
                        <div className="rp-file-item-top">
                          <span className="rp-file-name">{file.fileName}</span>
                          <span className={`rp-expiry-dot ${dotClass}`}>{days}</span>
                        </div>
                        <div className="rp-file-meta">
                          <span className="rp-uploader-name">{file.uploaderName}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <div className={`rp-dropdown-menu ${dropdownOpen ? 'show' : ''}`} ref={dropdownRef}>
                <div className="rp-menu-item" onClick={() => { fileInputRef.current.accept = 'image/*'; fileInputRef.current.click(); setDropdownOpen(false); }}>Photos</div>
                <div className="rp-menu-divider"></div>
                <div className="rp-menu-item" onClick={() => { fileInputRef.current.accept = '*/*'; fileInputRef.current.click(); setDropdownOpen(false); }}>Files</div>
              </div>
            </div>
          </div>

          <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileUpload} />
        </div>
      </div>

      {/* Rate Adjustment Modal */}
      {rateModalOpen && (
        <div className="rp-modal-overlay rp-modal-open" onClick={() => setRateModalOpen(false)}>
          <div className="rp-modal" onClick={e => e.stopPropagation()}>
            <button className="rp-modal-close" onClick={() => setRateModalOpen(false)}>✕</button>
            <div className="rp-modal-title">Adjust weekly rate</div>
            <p className="rp-modal-sub">The tutee will be notified of this change and the new rate will come into effect on the next billing cycle.</p>
            <div className="rp-modal-field">
              <label className="rp-modal-label">Weekly rate (R)</label>
              <div className="rp-rate-input-wrap">
                <span className="rp-rate-prefix">R</span>
                <input type="number" className="rp-modal-input" placeholder="e.g. 280" min="0" step="0.01" value={newRate} onChange={(e) => setNewRate(e.target.value)} />
              </div>
            </div>
            <div className="rp-modal-actions">
              <button className="rp-btn-cancel" onClick={() => setRateModalOpen(false)}>Cancel</button>
              <button className="rp-btn-confirm" onClick={updateRate}>Update Rate</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {uploadToast && (
        <div className={`rp-toast rp-toast-${uploadToast.type}`}>
          <span className="rp-toast-icon">{uploadToast.type === 'success' ? '✓' : uploadToast.type === 'error' ? '✗' : '⟳'}</span>
          <span className="rp-toast-message">{uploadToast.message}</span>
        </div>
      )}
    </div>
  );
};

export default RequestPortal;